import { Injectable } from '@angular/core';
import { ApiOptions, ApiService } from 'src/app/core/services/api.service';
import { map, tap, take } from 'rxjs/operators';
import { EntityExtendedInfo } from '../models/entity.models';
import { SportCategory } from 'src/app/models/categories.model';
import { of, Observable, combineLatest, BehaviorSubject } from 'rxjs';
import { Permission, User, LoggedUser, UserDetails, UserDetail, UserExclusionRequest } from 'src/app/models/Users';
import { Store } from '@ngrx/store';
import { selectAuthAfUser, selectAuthUserData } from 'src/app/core/store/selectors/authentication.selectors';
import { Entity, EntityInternalOptions, EntityType } from 'src/app/models/entities.model';
import { selectContextEntity } from 'src/app/core/store/selectors/context.selectors';
import { Alert } from 'src/app/models/alerts.model';
import { PermissionService } from '../../permissions/services/permission.service';
import { IntegrationKey } from '../../integration/models/integration_key.model';
import { UtilService } from 'src/app/core/services/util.service';
import { FamilyRelation } from '../../athletes/models/athlete-family-member.model';
import { Document, DocumentTypes } from '../../documents/models/document.models';
import { Action_Auth_SetAdminAccess, Action_Auth_Signout } from 'src/app/core/store/actions/authentication.actions';
import { Action_Context_Change } from 'src/app/core/store/actions/context.actions';
import { mapPayload } from 'src/app/core/classes/operators';
import { CacheGroup, CACHE_GROUP } from '../../cache/cache-table';
import { AFUser } from 'src/app/core/models/authentication.model';
import { UserHelper } from 'src/app/core/helpers/user.helpers';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { EntityValidatorComponent } from '../components/entity-validator/entity-validator.component';
import { EntityValidatorResponse } from '../components/entity-validator/entity-validator.models';
import { Championship } from '../../championship/models/championship.model';
import { Log } from 'src/app/models/logs.interface';
import { Match, MatchDelegation } from '../../championship/models/match.model';
import { Registration } from '../../registrations/models/registration.model';
import { Relation } from 'src/app/models/relations.model';
import { EntityHistoryDataResponse } from './entity_history_data_model';

export type EntityInfo = 'entity' | 'documents' | 'relations' | 'permissions' | 'integration_keys';

@Injectable({
  providedIn: 'root'
})
export class EntityService {

  entityCategoriesCache = {};

  private _context: Entity;
  private _loggedUser: LoggedUser;
  private _userFirebase: AFUser;

  private _loadEntityTimeout;
  private _requestedEntities: number[] = [];
  private _promises: { resolve: (data: any) => void, reject: (err: any) => void }[] = [];


  userDefaults: any;

  public contextPreferenceColors$ = this.store.select(selectContextEntity)
    .pipe(
      map(entity => {
        this._context = entity;
        return {
          brand: this.getEntityColor('brand', false),
          constrast: this.getEntityColor('contrast', false)
        };
      })
    );

  private _context$: BehaviorSubject<Entity> = new BehaviorSubject(null);
  private _loggedUser$: BehaviorSubject<LoggedUser> = new BehaviorSubject(null);
  private _userFirebase$: BehaviorSubject<AFUser> = new BehaviorSubject(null);


  private _logged$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public logged$: Observable<boolean> = this._logged$.asObservable();

  private _changeContextModal$ = new BehaviorSubject(false);

  changedPreferences = {};
  preferencesSyncScheduler;


  constructor(
    private api: ApiService,
    private store: Store<any>,
    public sPermission: PermissionService,
    private util: UtilService,
    private modal: MatDialog
  ) {
    const context$ = this.store.select(selectContextEntity);
    const user$ = this.store.select(selectAuthUserData);
    const userFB$ = this.store.select(selectAuthAfUser);

    combineLatest([context$, user$, userFB$]).subscribe(([context, user, userFB]) => {
      this._context = context;
      this._loggedUser = user;
      this._userFirebase = userFB;
      this.loadUserDefaults();
      this._context$.next(this._context);
      this._logged$.next(!!context);
      this._loggedUser$.next(user);
      this._userFirebase$.next(userFB)
    });

  }

  get changeContext$() {
    return this._changeContextModal$.asObservable();
  }
  get loggedUser() {
    if (!this._loggedUser) {
      return null;
    }
    const NU = Object.assign({}, this._loggedUser);
    return new LoggedUser(NU);
  }

  get context() {
    if (!this._context) {
      return null;
    }
    return this._context;
  }

  get context$() {
    return this._context$.asObservable();
  }

  get user$() {
    return this._loggedUser$.asObservable();
  }

  get userFirebase$() {
    return this._userFirebase$.asObservable();
  }
  get userFirebase() {
    return this._userFirebase;
  }

  getRegisteredDocuments(entityID: number) {
    return this.api.get<{
      find(arg0: (d: any) => void): unknown; documents: Document[] 
}>
      (`entity/${entityID}/documents`, {}).pipe(mapPayload);
  }

  setContext(entity: Entity, options?: { redirectPath?: string[] }) {
    if (options) {
      if (options.redirectPath) {
        localStorage.setItem('redirectOnContextChange', JSON.stringify(options.redirectPath));
      }
    }
    this.store.dispatch(new Action_Context_Change(entity));
  }

  getUserContextByType(entityType: string) {

    if (!this.loggedUser) {
      return null;
    }

    return this.loggedUser.permissions[entityType];
  }

  getUserContexts() {
    let contexts: Entity[] = [];
    if (!this.loggedUser) {
      return null;
    }

    Object.keys(this.loggedUser.permissions).forEach(key => {
      let entities: Entity[] = this.loggedUser.permissions[key];
      if (entities && entities.length > 0) {
        contexts.push(...entities);
      }
    })

    return contexts;
  }


  loadUserDefaults() {
    this.userDefaults = {};
    if (this.loggedUser) {
      const d = localStorage.getItem('ud_' + this.loggedUser.id);
      if (!d) {
        return;
      }
      this.userDefaults = JSON.parse(d);
    }
  }

  getFamilyMembers(entityUID: string): Observable<FamilyRelation[]> {
    return this.api.get<FamilyRelation[]>(`entity/${entityUID}/family_members`).pipe(map(res => res.payload));
  }

  getEntityInfo(entityID: number, info: EntityInfo[]) {
    const params: any = {};
    if (info && info.length > 0) {
      const populate = info.join(',');
      params.populate = populate;
    }

    return this.api.get<EntityExtendedInfo>(`entity/${entityID}/extended`, params)
      .pipe(map(_res => _res.payload));
  }

  getEntityRaw<T>(entityID: number): Observable<T> {
    return this.api.get<T>(`entity/${entityID}`, undefined, { acceptCache: '1h', cacheGroup: 'entity' })
      .pipe(mapPayload);
  }

  getCategories(entityID: number, sportID: number = 151): Observable<SportCategory[]> {
    if (this.entityCategoriesCache[sportID]) {
      if (this.entityCategoriesCache[sportID][entityID]) {
        return of(this.entityCategoriesCache[sportID][entityID]);
      }
    }

    return this.api.get<SportCategory[]>(`entity/${entityID}/sport/${sportID}/categories`).pipe(
      map(_res => _res.payload.map(cat => new SportCategory(cat))),
      tap((_categories) => {
        if (!this.entityCategoriesCache[sportID]) {
          this.entityCategoriesCache[sportID] = [];
        }
        this.entityCategoriesCache[sportID][entityID] = _categories;
      })
    );
  }

  getEntityRoles(entityID: number): Promise<Permission> {
    return new Promise((resolve, reject) => {
      this.store.select(selectAuthUserData).pipe(take(1)).subscribe(user => {
        if (!user.roles || user.roles.length == 0) {
          resolve(null);
          return;
        }

        const role = user.roles.find(r => r.target_id == entityID);
        if (!role) {
          resolve(null);
          return;
        }
        resolve(role);
      });
    });

  }

  getEntityColor(type: 'brand' | 'contrast', inverted = false) {
    let brand = inverted ? 'white' : '#011131';
    let contrast = inverted ? '#011131' : 'white';

    if (!this._context) {
      if (type === 'brand') { return brand; }
      return contrast;
    } else {

      if (this._context.preferences && this._context.preferences.theme_active) {
        const pref = this._context.preferences;
        const _brand = pref.brand_color;
        const _contrast = pref.brand_contrast;

        if (_brand && !inverted) { brand = _brand; }
        if (_brand && inverted) { brand = _contrast; }
        if (_contrast && !inverted) { contrast = _contrast; }
        if (_contrast && inverted) { contrast = _brand; }
      }
    }
    if (type === 'brand') { return brand; }
    return contrast;
  }

  getBlocks(entityID: number) {
    return this.api.get(`entity/${entityID}/blocks`).pipe(map(_res => _res.payload));
  }

  unblockEntities(entityID: number, entities: number[]) {
    return this.api.patch(`entity/${entityID}/unblock`, { entities }).pipe(map(_res => _res.payload));
  }

  setAvailability(entityID: number, available: string) {
    return this.api.patch(`entity/${entityID}/set_availability`, {}, { available }).pipe(map(_res => _res.payload));
  }

  startEntityLoader() {
    this._loadEntityTimeout = setTimeout(() => {
      this.api.get<Entity[]>(`entity/get_by_ids`, { ids: this._requestedEntities.join(',') })
        .subscribe(
          res => {
            this._promises.forEach(p => p.resolve(res.payload));
          },
          err => {
            this._promises.forEach(p => p.reject(err));
          });
    }, 500);
  }

  getEntities(ids: number[]) {
    if (this._loadEntityTimeout) {
      clearTimeout(this._loadEntityTimeout);
    }

    return new Promise((resolve, reject) => {
      this._requestedEntities = this._requestedEntities.concat(ids);
      this._promises.push({ resolve, reject });
    });

  }

  sendAlert(targetID: number, data: Partial<Alert>) {
    data.target_entity = targetID;
    return this.api.post(`entity/alert`, data).pipe(map(res => res.payload));
  }

  getUserDefault(param: string, defaultValue: any) {

    const user = this.loggedUser;

    if (user && user.preferences && user.preferences.usability && Object.hasKey(user.preferences.usability, param)) {
      const value = user.preferences.usability[param];
      return value;
    }

    if (defaultValue) {
      this.setUserDefault(param, defaultValue);
    }

    return defaultValue;
  }

  setUserDefault(param: string, value: any) {
    const user = this.loggedUser;

    if (!user.preferences) {
      user.preferences = {};
    }

    if (!user.preferences.usability) {
      user.preferences.usability = {};
    }

    user.preferences.usability[param] = value;
    this.changedPreferences[param] = value;
    this.savePreferences();
  }

  savePreferences() {
    if (this.preferencesSyncScheduler) {
      return;
    }

    this.preferencesSyncScheduler = setTimeout(() => {
      clearTimeout(this.preferencesSyncScheduler);
      this.preferencesSyncScheduler = null;
      if (this.changedPreferences && Object.keys(this.changedPreferences).length > 0) {
        this.syncPreferences();
      }
    }, 15000);

  }

  syncPreferences() {
    const updates = Object.keys(this.changedPreferences).length > 0 ? this.changedPreferences : null;

    const invalidate: CacheGroup[] = updates ? ['user'] : null;
    this.api.patch(`users/sync_usability_preferences`,
      updates,
      undefined,
      { silent: true, invalidateOnSuccess: invalidate }
    ).pipe(mapPayload)
      .subscribe(
        resp => {
          this.changedPreferences = {};
        },
        err => { this.savePreferences(); });
  }

  saveInternalOptions(entityUID: string, options: EntityInternalOptions) {
    return this.api.post<EntityInternalOptions>(`entity/${entityUID}/internal_options`, options).pipe(map(res => res.payload));
  }

  generateIntegrationKey(entityId: number, options: Partial<IntegrationKey>) {
    options.target_id = entityId;
    return this.api.post<IntegrationKey>(`entity/${entityId}/integration_key`, options).pipe(map(res => {
      return {
        integration: res.payload,
        extra: res.extraInfo
      };
    }));
  }

  revokeIntegrationKey(ik: IntegrationKey) {
    return this.api.delete(`entity/${ik.target_id}/integration_key/${ik.id}`).pipe(map(res => res.payload));
  }

  saveSelfUser(entityUID: string, userUID: string) {
    return this.api.post(`entity/${entityUID}/vinculate_self_user`, { user_uid: userUID }).pipe(map(res => res.payload));
  }

  async entityIsSamePersonAsUser() {

    const entity = new Entity(this.context);
    const user = new User(this.loggedUser);

    if (entity.source_name === 'users') {
      return true;
    }

    if (entity.is_company) {
      return false;
    }

    if (!entity.self_user) {

      // must be at least document or rg with the same information
      let isSamePerson = false;

      if (entity.document && entity.document === user.document) {
        isSamePerson = true;
      }

      if (Object.hasKey(entity as any, 'rg') && Object.hasKey(user, 'rg')) {
        const entityRg = (entity as any).rg;
        const userRg = user.rg;
        if (entityRg === userRg) {
          isSamePerson = true;
        }
      }

      if (!isSamePerson) {
        const error = new Error('Esta entidade não é o usuário logado');
        error.name = 'notSamePerson';
        throw error;
      }


      // save Self Entity
      this.util.loader.show(`Ajustado alguns detalhes...`);
      const saved = await this.saveSelfUser(entity.entity_uid, user.entity_uid).toPromise();
      this.util.loader.close();
      return true;


    } else {
      if (+entity.self_user !== +user.id) {
        const error = new Error('Esta entidade não é a mesma pessoa que o usuário');
        error.name = 'otherPerson';
        throw error;
      }
      return true;
    }
  }

  isLegalResponsible() {
    return this.sPermission.isLegalResponsible();
  }

  hasPermission(permission: string, targetType?: 'entity' | 'internal') {
    return this.sPermission.hasPermission(permission, targetType);
  }

  get isInstancePermission() {
    return this.sPermission.isIntancePermission;
  }

  get isInternal() {
    return ['I', 'A'].includes(this.loggedUser?.user_type);
  }

  get isMasterAdmin() {
    return ['A'].includes(this.loggedUser.user_type);
  }

  openEntityAsAdmin(entity: Entity) {

    const message$: BehaviorSubject<string> = new BehaviorSubject(`Setando permissões de sessão da conta ${entity.name}`);
    this.util.loader.show(message$);
    this.store.dispatch(new Action_Auth_SetAdminAccess(entity));

    setTimeout(() => {
      message$.next(`Alterando contexto...`);
    }, 1000);

    setTimeout(async () => {
      this.util.loader.close();
      this.util.toast.success(`permissão de sessão definida. Ao atualizar a tela esta permissão será perdida.`, { timer: 8 });
      this.store.dispatch(new Action_Context_Change(entity));
    }, 2000);
  }

  toggleContextChanger(visible?: boolean) {
    let _visible = this._changeContextModal$.value;
    if (visible != null) {
      _visible = visible;
    } else {
      _visible = !_visible;
    }
    this._changeContextModal$.next(_visible);
  }

  async cleanUserCache() {


    const res = await this.util.dialog.dialog({
      message: 'Qual tipo de cache deseja limpar?',
      title: 'Limpeza de cache',
      buttons: [
        {
          text: 'Cancelar',
          color: 'primary',
        },
        {
          text: 'Todos',
          color: 'primary',
        },
        {
          text: 'Performance',
          color: 'primary',
          default: true,
        }
      ]
    });

    if (!res || res == 'Cancelar') {
      return;
    }

    if (res == 'Todos') {
      const confirm = await this.util.dialog.confirmWithOptions({
        title: 'Limpeza de cache',
        message: `Esta ação irá <span style="color:red;font-weight:bold">excluir</span> todos os dados armazenados pelo GIOCO e seus fornecedores,
        incluindo as infomações de autenticação. Por este motivo após a exclusão será necessário entrar novamente no sistema.<br>
        lembre-se que quando você acessar novamente o sistema por este computador os dados serão armazenados novamente e isto pode resultar em um maior consumo de dados da sua internet.
        `,
        noText: 'LIMPAR',
        yesText: 'FECHAR',
        disableClose: true
      });

      if (confirm) {
        return;
      }

      const message$ = new BehaviorSubject('Deslogando usuário do sistema...');
      this.util.loader.show(message$);

      this.store.dispatch(new Action_Auth_Signout());
      await Promise.delayed(1000);
      message$.next('Excluindo os dados armazenados nesta máquina...');
      if (localStorage) {
        localStorage.clear();
      }

      const _indexedDB: any = indexedDB ||
        window.indexedDB || (window as any).mozIndexedDB
        || (window as any).webkitIndexedDB
        || (window as any).msIndexedDB;

      if (_indexedDB.databases) {
        await _indexedDB.databases()
          .then((r: any) => {
            const length = r.length;
            for (let i = 0; i < length; i++) {
              _indexedDB.deleteDatabase(r[i].name);
            }
          });
      }

      await Promise.delayed(1000);

      message$.complete();
      await this.util.dialog.inform('Dados excluidos com sucesso', 'Tudo certo!');
      window.location.reload();
    }

    if (res == 'Performance') {
      const message$ = new BehaviorSubject('Limpando cache de performance...');
      this.util.loader.show(message$);
      await Promise.delayed(2000);
      this.api.invalidatePerformanceCache()
      this.util.loader.close();
    }
  }

  public getSelfEntities(apiOptions?: ApiOptions) {
    return this.api.get<Entity[]>(`entity/user_self_entities`, {}, apiOptions).pipe(map(_res => _res.payload));
  }

  public getLoggedUserDetails(details: UserDetail[], apiOptions?: ApiOptions) {
    const _params: any = {};
    details.forEach(d => _params[d] = 'true');
    return this.api.get<UserDetails>('users/logged_data_details',
      _params,
      apiOptions
    ).pipe(mapPayload);
  }

  public getLoggedUserData(apiOptions?: ApiOptions) {
    return this.api.get<User>('users/logged_data',
      {}, apiOptions
    );
  }

  public requestAccountExclusion(params: { phone: string, reason: string }) {
    const { phone, reason } = params;
    return this.api.post<UserExclusionRequest>(`users/request_account_exclusion`, { phone, reason }).pipe(mapPayload);
  }

  public cancelAccountExclusion(validation_id: number) {
    return this.api.post<UserExclusionRequest>(`users/cancel_account_exclusion`, { validation_id }, null, { invalidateOnSuccess: ['user'] }).pipe(mapPayload);
  }

  public search(term: string, entityType?: EntityType): Observable<Entity[]> {
    const params: any = { term };
    if (entityType) {
      params.sources = entityType;
    }

    return this.api.get<Entity[]>(`entity/search`, params).pipe(mapPayload);
  }

  async getContextEntities() {
    const entities = await this.store.select(selectAuthUserData).pipe(
      take(1),
      map(user => {
        if (!user) {
          return [] as Entity[];
        }
        return UserHelper.getPermissionEntities(user.permissions);
      })
    ).toPromise();

    return entities;
  }

  openValidator(entity: any) {

    // if (!this.loggedUser.validated) {
    //   this.util.dialog.inform(`Você precisa aguardar a aprovação do seu cadastro pela 4BP para poder
    //     efetuar a validação de outros cadastros32`
    //     , `Operação não permitida`);
    //   return;
    // }

    // if (!this.context.validated) {
    //   this.util.dialog.inform(`Você precisa aguardar a aprovação do cadastro desta liga para poder
    //     efetuar a validação de outros cadastros`
    //     , `Operação não permitida`);
    //   return;
    // }

    const ref: MatDialogRef<EntityValidatorComponent, EntityValidatorResponse> = this.modal.open(EntityValidatorComponent, {
      width: '100%',
      height: '100%',
      minWidth: '100%',
      minHeight: '100%',
      data: {
        entity_id: entity.id,
        entity_name: entity.name
      }
    });

    return ref;

  }

  getEntityhistoryData(entityUID: string, apiOptions?: ApiOptions) {
    return this.api.get<EntityHistoryDataResponse>(`logs/entity/${entityUID}/history_data`).pipe(mapPayload);
  }

}
